/**
 * Nothing renders until the person is signed in and cpg.computer says they may use this app. The server enforces
 * the same rule on every /api route; this is the page-side half, so nobody sees an empty shell.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import type { Access } from '../shared/types'
import { Button, Notice } from '../ui'
import { AccessContext } from './access'
import { ApiError, apiGet } from './api'
import { configureReporting } from './reportError'
import { RETURN_KEY, useSession } from './session'

export function Gate({ children }: { children: ReactNode }) {
  const session = useSession()
  const location = useLocation()
  const here = location.pathname + location.search
  const onCallback = location.pathname === '/callback'
  const [access, setAccess] = useState<Access | null>(null)
  const [problem, setProblem] = useState<ApiError | null>(null)
  const [attempt, setAttempt] = useState(0)

  // Not signed in: straight to sign-in, then back here. A session that ended mid-use waits for the button instead.
  useEffect(() => {
    if (!session.isLoading && !session.isAuthenticated && !session.error && !session.ended) {
      session.login(onCallback ? '/' : here)
    }
  }, [session.isLoading, session.isAuthenticated, session.error, session.ended, onCallback, here, session])

  // Signed in: ask the server who we are to this app. A 401 here gets the same one silent refresh as useApi.
  const { isAuthenticated, getToken, refresh, end } = session
  useEffect(() => {
    if (!isAuthenticated) return
    let live = true
    setProblem(null)
    apiGet<Access>('/api/me', { getToken, refresh, end }).then(
      (a) => {
        if (!live) return
        configureReporting({ getToken, userId: a.user.sub })
        setAccess(a)
      },
      (e: unknown) => live && setProblem(e instanceof ApiError ? e : new ApiError(0, String(e))),
    )
    return () => {
      live = false
    }
  }, [isAuthenticated, getToken, refresh, end, attempt])

  if (session.error) {
    return (
      <Notice title="Sign-in didn't finish" body={session.error}>
        <Button variant="primary" onClick={() => session.login('/')}>Sign in again</Button>
      </Notice>
    )
  }
  // The sign-in ran out mid-session and the silent refresh failed (here or in any screen's useApi).
  if (session.ended || problem?.status === 401) {
    return (
      <Notice title="Your session ended" body="Sign in again to carry on where you were.">
        <Button variant="primary" onClick={() => session.login(here)}>Sign in again</Button>
      </Notice>
    )
  }
  if (session.isLoading || !session.isAuthenticated) return <Notice title="Signing you in…" />

  if (problem?.status === 403) {
    return (
      <Notice
        title="You don't have access to this app"
        body="You're signed in, but this app hasn't been shared with you. Ask the person who sent you the link to share it, or sign in with a different account."
      >
        <Button variant="primary" onClick={session.logout}>Sign out</Button>
      </Notice>
    )
  }
  if (problem) {
    return (
      <Notice title="We couldn't check your access" body="This is usually brief. Try again in a moment.">
        <Button variant="primary" onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
        <Button onClick={session.logout}>Sign out</Button>
      </Notice>
    )
  }
  if (!access) return <Notice title="Opening…" />

  if (onCallback) {
    const back = sessionStorage.getItem(RETURN_KEY) || '/'
    sessionStorage.removeItem(RETURN_KEY)
    return <Navigate to={back.startsWith('/callback') ? '/' : back} replace />
  }
  return <AccessContext.Provider value={access}>{children}</AccessContext.Provider>
}
